import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { carouselApi, CarouselSlide } from '../../api/tendApi';
import {
  PublishedMedia,
  resolvePublishedMedia,
  resolveSlideMedia,
  slideHasRender,
  absMediaUrl,
} from '../../utils/publishedMedia';

interface PublishedMediaViewerProps {
  /** Hydrated tendv2 item (may carry injected `slides` on state='p' listings). */
  item: any;
  /** Slides the card already loaded (carousel mode) — saves a fetch. */
  slides?: CarouselSlide[];
  /** Channel aspect bucket CSS (e.g. '4/5') — renders are produced at this
   * bucket, so object-cover is lossless. Falls back to measured ratio. */
  aspectCss?: string;
}

/**
 * Read-only display of the media that actually went out with the publish —
 * replaces the editable overlay/media-strip editor once an item is
 * published. Single image/video renders plain; carousels and video series
 * get arrows + dots + an n/N chip (TendedPlatformPreview chrome), no swipe,
 * no edit affordances.
 */
const PublishedMediaViewer: React.FC<PublishedMediaViewerProps> = ({
  item,
  slides,
  aspectCss,
}) => {
  const [fetchedSlides, setFetchedSlides] = useState<CarouselSlide[] | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [measuredRatio, setMeasuredRatio] = useState<number | null>(null);

  const isMultiSlide = item.content_mode === 'carousel' || item.content_mode === 'video_series';
  const injectedSlides: any[] = Array.isArray(item.slides) ? item.slides : [];
  const baseSlides: any[] = slides && slides.length > 0
    ? slides
    : injectedSlides.length > 0
      ? injectedSlides
      : fetchedSlides || [];

  // Published multi-slide item mounted from a listing that doesn't inject
  // slides (e.g. a repost-requeued card in a channel feed): fetch once.
  const needsFetch = isMultiSlide && baseSlides.length === 0 && fetchedSlides === null;
  useEffect(() => {
    if (!needsFetch) return;
    let alive = true;
    carouselApi
      .listSlides(item.id)
      .then((rows) => { if (alive) setFetchedSlides(rows); })
      .catch(() => { if (alive) setFetchedSlides([]); });
    return () => { alive = false; };
  }, [needsFetch, item.id]);

  const medias: PublishedMedia[] = useMemo(() => {
    const fromSlides = baseSlides
      .map(resolveSlideMedia)
      .filter((m): m is PublishedMedia => m !== null);
    // Slides win only when they carry RENDER outputs (real published
    // carousels/series). Raw-only slides are editor state synthesized from
    // the media strip — they must not beat the item's rendered publish
    // output (the wrong-raw-video-over-published-render bug, octakitty
    // 4096). Raw slides remain the last resort when nothing rendered.
    const slidesRendered = baseSlides.some(slideHasRender);
    if (fromSlides.length > 0 && slidesRendered) return fromSlides;
    const whole = resolvePublishedMedia(item);
    if (whole) return [whole];
    return fromSlides;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, baseSlides.length, fetchedSlides]);

  const clampedIndex = Math.min(activeIndex, Math.max(0, medias.length - 1));
  const media = medias[clampedIndex];

  if (!media) {
    return (
      <div className="mb-3 p-6 rounded-lg border border-dashed border-gray-300 bg-gray-50 text-xs text-gray-500 text-center">
        Published media unavailable
      </div>
    );
  }

  const containerStyle: React.CSSProperties = {
    aspectRatio: aspectCss || (measuredRatio ? String(measuredRatio) : '1 / 1'),
  };

  const measureVideo = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const el = e.currentTarget;
    if (el.videoWidth && el.videoHeight) setMeasuredRatio(el.videoWidth / el.videoHeight);
  };
  const measureImage = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const el = e.currentTarget;
    if (el.naturalWidth && el.naturalHeight) setMeasuredRatio(el.naturalWidth / el.naturalHeight);
  };

  return (
    <div
      className="mb-3 relative w-full overflow-hidden rounded-lg bg-black"
      style={containerStyle}
    >
      {media.kind === 'video' ? (
        <video
          key={media.url}
          src={absMediaUrl(media.url)}
          controls
          muted
          playsInline
          loop
          preload="metadata"
          className="w-full h-full object-cover"
          onLoadedMetadata={measureVideo}
        />
      ) : (
        <img
          key={media.url}
          src={media.url}
          alt="Published media"
          loading="lazy"
          className="w-full h-full object-cover"
          onLoad={measureImage}
        />
      )}

      {medias.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => setActiveIndex((clampedIndex - 1 + medias.length) % medias.length)}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 text-white
                       flex items-center justify-center hover:bg-black/80 transition-colors"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => setActiveIndex((clampedIndex + 1) % medias.length)}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 text-white
                       flex items-center justify-center hover:bg-black/80 transition-colors"
            aria-label="Next slide"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-black/60 text-white text-xs">
            {clampedIndex + 1}/{medias.length}
          </span>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
            {medias.map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setActiveIndex(index)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === clampedIndex ? 'bg-white' : 'bg-white/40 hover:bg-white/60'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default PublishedMediaViewer;
